import React, { useState } from "react"
import { Editor } from "core/editor"
import { SoftBreak } from "core/editor/plugins/soft-break"
import { serialize } from "core/editor/serializer"
import {
  SelectionToolbar,
  parse,
  LinkToolbarButton as LinkButton
} from "@convertkit/editor"
import Icon from "@kit/design/icon"
import { createValue } from "./utils/create-value"
import { getValue } from "./utils/get-value"
import throttle from "lodash/throttle"
import { sanitizeHTML } from "utils/sanitize-html"

const headingMap = {
  h1: "heading-one",
  h2: "heading-two",
  h3: "heading-three",
  h4: "heading-four",
  h5: "heading-five",
  h6: "heading-six"
}

const createSchema = ({ type }) => ({
  document: {
    nodes: [
      {
        match: { type },
        min: 1,
        max: 1
      }
    ],
    normalize: (editor, error) => {
      switch (error.code) {
        case "child_type_invalid":
          editor.setNodeByKey(error.child.key, {
            type
          })
          return

        default:
          return
      }
    }
  }
})

const buttons = [
  "bold",
  "underline",
  "italic",
  "alignment",
  LinkButton({
    component: ({ menu }) => (
      <button
        className="convertkit-editor-selection-toolbar-button"
        onClick={menu.open}
      >
        <Icon name="link" />
      </button>
    )
  })
]

const plugins = [SoftBreak()]

export const HeaderEditor = ({
  className,
  content,
  name,
  value,
  defaults,
  onClick,
  onChange,
  style,
  tag,
  editing
}) => {
  // TODO: Some rearchitecture of the builder could reduce the need to throttle updates here.
  // Currently, we can't have every keystroke updating the root builder state, causing every
  // component to rerender.
  const throttleChange = throttle(onChange, 1000, { trailing: true })

  const [schema] = useState(createSchema({ type: headingMap[tag] }))

  const [editorValue, setEditorValue] = useState(() => {
    if (editing) {
      if (content && !value) {
        // TODO: Remove this when all header values are using Slate values
        return parse(content)
      }

      return getValue(
        value,
        createValue(defaults.content, { type: headingMap[tag] })
      )
    }
  })

  const attributes = {
    className,
    style,
    "data-element": name
  }

  if (!editing) {
    return (
      <div
        {...attributes}
        dangerouslySetInnerHTML={{
          __html: sanitizeHTML(
            getValue(value, content, defaults, { serialize })
          )
        }}
      />
    )
  }

  const handleChange = ({ value }) => {
    setEditorValue(value)
    throttleChange({
      content: serialize(value),
      value: JSON.stringify(value)
    })
  }

  return (
    <Editor
      value={editorValue}
      onClick={onClick}
      onChange={handleChange}
      plugins={plugins}
      schema={schema}
      {...attributes}
    >
      <SelectionToolbar buttons={buttons} />
    </Editor>
  )
}
